/* External */
import {
  memo,
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import sortedIndex from 'lodash-es/sortedIndex';
import { List, Record } from 'immutable';
import debounce from 'lodash-es/debounce';

/* Components */
import ScrollView from '../ScrollView';
import VirtualizedItem from './VirtualizedItem';
import { noOp } from '../../utils/functions';

function getElementIndexByScroll(scrollValue, itemSizeOrAccumulatedItemSizes) {
  let index;
  index = typeof itemSizeOrAccumulatedItemSizes === 'number'
    ? Math.ceil(scrollValue / itemSizeOrAccumulatedItemSizes)
    : sortedIndex(itemSizeOrAccumulatedItemSizes, scrollValue);

  index -= 1;
  return index < 0 ? 0 : index;
}

const VirtualizedItemProps = Record({
  index: -1,
  position: -1,
  height: -1,
});

const TIME_TO_WAIT_TO_MOUNT_NEW_ITEMS = 100;

function VirtualizedList({
  className,
  style: styleProp,
  outerElementType: OuterElementType,
  height,
  itemCount,
  itemSize,
  overscanCount,
  renderer,
  onScroll,
  children,
  resetScrollPosition,
  setItemsHeight,
  initialScrollPosition,
  propagateEventOnReachTop,
  initialMaxOffset,
  itemsContainerClassName,
}) {
  const [scrollPosition, setScrollPosition] = useState(initialScrollPosition);
  const virtualizedItemsRef = useRef(undefined);

  const getVirtualizedItems = useCallback(() => {
    if (!virtualizedItemsRef.current) {
      virtualizedItemsRef.current = List();
    }

    return virtualizedItemsRef.current;
  }, []);

  const accumulatedItemSizes = useMemo(() => {
    if (typeof itemSize === 'function') {
      const accumulatedItemSizesArray = [];
      accumulatedItemSizesArray.push(0);
      let accumulator = 0;
      for (let index = 0; index < itemCount; index += 1) {
        const sizeOfThisItem = itemSize(index);
        accumulator += sizeOfThisItem;
        accumulatedItemSizesArray.push(accumulator);
      }

      return accumulatedItemSizesArray;
    }

    return undefined;
  }, [itemCount, itemSize]);

  const scrollableHeight = typeof itemSize === 'function'
    ? accumulatedItemSizes[itemCount]
    : itemCount * itemSize;

  const firstVisibleElementIndex = useMemo(
    () => getElementIndexByScroll(scrollPosition, accumulatedItemSizes || itemSize),
    [accumulatedItemSizes, itemSize, scrollPosition],
  );

  const lastVisibleElementIndex = useMemo(
    () => getElementIndexByScroll(scrollPosition + height, accumulatedItemSizes || itemSize),
    [accumulatedItemSizes, height, itemSize, scrollPosition],
  );

  virtualizedItemsRef.current = useMemo(
    () => getVirtualizedItems().withMutations((mutableList) => {
      mutableList.setSize(itemCount);
      for (let index = 0; index < itemCount; index += 1) {
        let position;
        let itemHeight;
        if (typeof itemSize === 'function') {
          itemHeight = accumulatedItemSizes[index + 1] - accumulatedItemSizes[index];
          position = accumulatedItemSizes[index];
        } else {
          itemHeight = itemSize;
          position = itemSize * index;
        }

        const currentValue = mutableList.get(index);
        const props = { index, position, height: itemHeight };

        mutableList.set(
          index,
          currentValue ? currentValue.merge(props) : VirtualizedItemProps(props),
        );
      }
    }),
    [accumulatedItemSizes, getVirtualizedItems, itemCount, itemSize],
  );

  const virtualizedItemsList = getVirtualizedItems();

  const virtualizedItemsToDisplay = useMemo(
    () => {
      let overScanElementsCounter = 0;
      let startIndex = firstVisibleElementIndex;
      while (startIndex > 0 && overScanElementsCounter < overscanCount) {
        startIndex -= 1;
        if (virtualizedItemsList.size > 0 && virtualizedItemsList.get(startIndex)?.height > 0) {
          overScanElementsCounter += 1;
        }
      }
      startIndex = startIndex < 0 ? 0 : startIndex;

      overScanElementsCounter = 0;
      let endIndex = lastVisibleElementIndex + 1;
      while (endIndex < itemCount && overScanElementsCounter < overscanCount) {
        endIndex += 1;
        if (virtualizedItemsList.size > 0 && virtualizedItemsList.get(endIndex - 1)?.height > 0) {
          overScanElementsCounter += 1;
        }
      }
      endIndex = endIndex > itemCount ? itemCount : endIndex;

      return virtualizedItemsList
        .slice(startIndex, endIndex)
        .map(({ index, position }) => (
          <VirtualizedItem
            key={index}
            index={index}
            position={position}
            renderer={renderer}
            firstVisibleElementIndex={firstVisibleElementIndex}
          />
        ));
    },
    [
      firstVisibleElementIndex,
      overscanCount,
      lastVisibleElementIndex,
      itemCount,
      virtualizedItemsList,
      renderer,
    ],
  );

  const itemsContainerStyle = useMemo(() => ({
    position: 'relative',
    height: `${scrollableHeight}px`,
  }), [scrollableHeight]);

  const debouncedSetScrollPosition = useMemo(
    () => debounce(setScrollPosition, TIME_TO_WAIT_TO_MOUNT_NEW_ITEMS, { leading: false }),
    [],
  );

  const handleScroll = useCallback(
    (newScrollPosition, maxScrollOffset) => {
      debouncedSetScrollPosition(newScrollPosition);
      if (onScroll) onScroll(newScrollPosition, maxScrollOffset);
    },
    [debouncedSetScrollPosition, onScroll],
  );

  const style = useMemo(() => ({
    ...styleProp,
    height,
  }), [height, styleProp]);

  useEffect(() => {
    const prevDebouncedSetScrollPosition = debouncedSetScrollPosition;
    return () => {
      prevDebouncedSetScrollPosition.cancel();
    };
  }, [debouncedSetScrollPosition]);

  useEffect(() => {
    if (setItemsHeight) {
      setItemsHeight(virtualizedItemsList);
    }
  }, [virtualizedItemsList, lastVisibleElementIndex, setItemsHeight]);

  return (
    <OuterElementType
      className={`dtv-html5-c-virtualized-list ${className}`}
      onScroll={handleScroll}
      resetScrollPosition={resetScrollPosition}
      style={style}
      initialScrollPosition={initialScrollPosition}
      propagateEventOnReachTop={propagateEventOnReachTop}
      initialMaxOffset={initialMaxOffset}
    >
      <div className={itemsContainerClassName} style={itemsContainerStyle}>
        {virtualizedItemsToDisplay}
      </div>
      {children}
    </OuterElementType>
  );
}

VirtualizedList.propTypes = {
  className: PropTypes.string,
  itemsContainerClassName: PropTypes.string,
  style: PropTypes.shape({}),
  outerElementType: PropTypes.elementType,
  height: PropTypes.number.isRequired,
  itemCount: PropTypes.number.isRequired,
  itemSize: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.func,
  ]).isRequired,
  overscanCount: PropTypes.number,
  renderer: PropTypes.elementType.isRequired,
  onScroll: PropTypes.func,
  children: PropTypes.node,
  resetScrollPosition: PropTypes.bool,
  setItemsHeight: PropTypes.func,
  initialScrollPosition: PropTypes.number,
  propagateEventOnReachTop: PropTypes.bool,
  initialMaxOffset: PropTypes.number,
};

VirtualizedList.defaultProps = {
  className: '',
  itemsContainerClassName: '',
  style: undefined,
  outerElementType: ScrollView,
  overscanCount: 1,
  onScroll: undefined,
  children: undefined,
  resetScrollPosition: false,
  initialScrollPosition: 0,
  setItemsHeight: noOp,
  propagateEventOnReachTop: false,
  initialMaxOffset: 0,
};

export default memo(VirtualizedList);
